import { PointerEvent, RefObject, useRef } from 'react';

/**
 * A hook to create pan event by pointer events
 * css properties `touch-action: pan-y;` & `user-select: none;` are required on the element
 * @returns pan info and pointer handlers
 */
export function usePan({
  cssVarEl,
  isFirstIndex,
  isLastIndex,
  panSize = 20,
  onPanNext = () => {},
  onPanPrev = () => {},
  onUpdate,
}: Partial<{
  /**
   * HTML element ref if you wish to set pan info to css variables to it
   */
  cssVarEl: RefObject<HTMLElement>;
  /**
   * Condition if the current active item is the first one.
   * This is helpful if you want to add different transition
   * when trying to go previous item even though current active one is the first item
   */
  isFirstIndex?: boolean;
  /**
   * Condition if the current active item is the last one.
   * This is helpful if you want to add different transition
   * when trying to go next item even though current active one is the last item
   */
  isLastIndex?: boolean;
  /**
   * Number to be panned
   */
  panSize: number;
  /**
   * Callback when panning to next
   */
  onPanNext: () => void;
  /**
   * Callback when panning to previous
   */
  onPanPrev: () => void;
  /**
   * Callback while swiping element
   * @param diff moving number of difference from initial point
   */
  onUpdate?: (diff: number) => void;
}> = {}) {
  const pointerRef = useRef({ flag: false, x: 0 });

  function updatePointerRef(flag: boolean, clientX: number, x?: number) {
    pointerRef.current = { flag, x: x ?? pointerRef.current.x };
    const diff = pointerRef.current.x - clientX;
    onUpdate?.(diff);
    if (!cssVarEl?.current) return;

    const shrink =
      (flag && diff > 0 && isLastIndex) || (flag && diff < 0 && isFirstIndex) ? Math.abs(diff) : 0;
    cssVarEl.current.style.setProperty('--shrink', `${shrink}`);
    cssVarEl.current.style.setProperty('--diff', `${diff}`);
  }

  function onPointerDown(event: PointerEvent<HTMLElement>) {
    updatePointerRef(true, event.clientX, event.clientX);
  }

  function onPointerMove(event: PointerEvent<HTMLElement>) {
    if (!pointerRef.current.flag) return;
    updatePointerRef(true, event.clientX);
  }

  function onPointerUp(event: PointerEvent<HTMLElement>) {
    const diff = pointerRef.current.x - event.clientX;
    if (diff > panSize) onPanNext();
    else if (diff < -panSize) onPanPrev();
    if (diff === 0) {
      updatePointerRef(false, 0, 0);
    } else {
      setTimeout(() => updatePointerRef(false, 0, 0));
    }
  }

  function onPointerLeave() {
    updatePointerRef(false, 0, 0);
  }

  function onPointerCancel() {
    updatePointerRef(false, 0, 0);
  }

  return {
    pointerRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave,
    onPointerCancel,
  };
}
